import urlParse from 'url-parse';

import { FeatureFlags } from '@neptune/config-core-domain';

import config from 'config';

import { featureFlagStore } from './featureFlag-storage';
import { FeatureFlag, FeatureFlagConfig, featureFlags } from './featureFlag-types';

let featureFlagsInitialized = false;

const featureFlagCache: Record<FeatureFlag, boolean | undefined> = {} as Record<
  FeatureFlag,
  boolean | undefined
>;

export type FeatureFlagState = Record<FeatureFlag, boolean | undefined> &
  Record<FeatureFlagConfig, any>;

function isFeatureFlag(text: any): text is FeatureFlag {
  return featureFlags.includes(text);
}

export function handleFeatureFlags() {
  const urlParams = urlParse(window.location.search, true);
  const setFeatureFlag = urlParams.query['setFeatureFlag'];
  const resetFeatureFlag = urlParams.query['resetFeatureFlag'];

  setFeatureFlag
    ?.split('~')
    .filter(isFeatureFlag)
    .forEach((flag) => {
      if (!featureFlags.includes(flag)) {
        return;
      }

      featureFlagStore.setFeatureFlag(flag, true);
      updateFeatureFlagCache(flag, true);

      const featureFlagConfig = urlParams.query['featureFlagConfig'];

      if (featureFlagConfig !== undefined) {
        featureFlagStore.setFeatureFlagConfig(flag, featureFlagConfig);
      }
    });

  resetFeatureFlag
    ?.split('~')
    .filter(isFeatureFlag)
    .forEach((flag) => {
      if (!featureFlags.includes(flag)) {
        return;
      }

      featureFlagStore.setFeatureFlag(flag, false);
      updateFeatureFlagCache(flag, false);
    });

  featureFlagsInitialized = true;
}

export const getAdminPanelEnabled = createFlagGetter('withAdminPanel');

export const getCsvFilePreviewLongCellImprovementsEnabled = createFlagGetter(
  'withCsvFilePreviewLongCellImprovements',
);

export const getDashboardsExportableToOtherProjectsEnabled = createFlagGetter(
  'withDashboardsExportableToOtherProjects',
);

export const getMarkdownWidgetsInDashboardsEnabled = createFlagGetter(
  'withMarkdownWidgetsDashboard',
);

export const getStringSeriesPreviewDownloadEnabled = createFlagGetter(
  'withStringSeriesPreviewDownload',
);
export const getFloatSeriesTextPreviewEnabled = createFlagGetter('withFloatSeriesTextPreview');
export const getMetricAggregatesHiddenEnabled = createFlagGetter('withMetricAggregatesHidden');
export const getImprovedImageScalingAlgorithmEnabled = createFlagGetter(
  'withImprovedImageScalingAlgorithm',
);
export const getShortenedMetricsEnabled = createFlagGetter('withShortenedMetrics');
export const getNewLegendPositioningEnabled = createFlagGetter('withNewLegendPositioning');
export const getMyRunsEnabled = createFlagGetter('withMyRuns');

export const getEmaSmoothingEnabled = createFlagGetter('withEmaSmoothing');
export const getGaussianSmoothingEnabled = createFlagGetter('withGaussianSmoothing');
export const getDoubleClickZoomOutEnabled = createFlagGetter('withDoubleClickZoomOut');

export const getChemicalFilesPreviewEnabled = createFlagGetter('withChemicalFilesPreview');

export const getInitiativesEnabled = createFlagGetter('withInitiatives');
export const getNonFiniteValuesEnabled = createFlagGetter('withNonFiniteValues');

export const getReportDslEditorEnabled = createFlagGetter('withReportDslEditor');
export const getWidgetDslEditorEnabled = createFlagGetter('withWidgetDslEditor');

export const getNewChartAllowedEnabled = createFlagGetter('withNewChartAllowed');
export const getNewChartEnabledRaw = createFlagGetter('withNewChart');
export const getNewChartEnabled = () => getNewChartAllowedEnabled() && getNewChartEnabledRaw();

// UDF (custom code) widgets
export const getUdfWidgetEnabled = (() => {
  const flagGetter = createFlagGetter('withUdfWidget');

  return () => {
    // If UDF sandbox is not enabled, always return false
    if (!config.udfSandboxEnabled) {
      return false;
    }

    return flagGetter();
  };
})();

export const getAssociatedRunsOptionEnabled = createFlagGetter('withAssociatedRunsOption');

export const getImprovedTooltipEditingEnabled = createFlagGetter('withImprovedTooltipEditing');

export const getScreenshotLegendEnabled = createFlagGetter('withScreenshotLegend');

export const getDashboardLockEnabled = createFlagGetter('withDashboardLock');
export const getPulseCheckSurveyEnabled = createFlagGetter('withPulseCheckSurvey');
export const getTristateSelectionEnabled = createFlagGetter('withTristateSelection');
export const getRunColorListsEnabled = createFlagGetter('withRunColorLists');

/**
 * Single creation gate for Vega-Lite evaluation widgets. Development and distribution builds enable
 * it by default. Reading, rendering and editing existing widgets never consult it, so an explicit
 * false only removes creation from widget pickers.
 */
export const getVegaLiteWidgetsCreationEnabled = createFlagGetter('withVegaLiteWidgets');

export const getSlackShareEnabled = createFlagGetter('withSlackShare');

function createFlagGetter(key: FeatureFlag & keyof FeatureFlags): () => boolean {
  if (!featureFlagsInitialized) {
    handleFeatureFlags();
  }

  return () => {
    if (
      !featureFlagStore.isDbPersistedConfigStoreInitialized() &&
      !featureFlagStore.isDbPersistedKeySafeBeforeInitialization(key)
    ) {
      throw new Error(
        `Feature flag ${key} is not safe to be read before initialization.
       Please ensure that the config store is initialized before using it.
       Or mark it as safe to be read before initialization in
       'DB_PERSISTED_KEYS_SAFE_BEFORE_INITIALIZATION'`,
      );
    }

    if (key in featureFlagCache) {
      return featureFlagCache[key] ?? config[key] ?? false;
    }

    const value = featureFlagStore.getFeatureFlag(key);
    updateFeatureFlagCache(key, value);

    if (value !== undefined) {
      return value;
    }

    return config[key] ?? false;
  };
}

function updateFeatureFlagCache(key: FeatureFlag, value: boolean | undefined) {
  featureFlagCache[key] = value;
}
